import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, FolderKanban, Users, ArrowUpDown } from "lucide-react";
import ProjectCard from "@/components/projects/ProjectCard";
import ProjectForm from "@/components/projects/ProjectForm";
import ProjectTeamManager from "@/components/projects/ProjectTeamManager";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export default function Projects() {
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showTeamManager, setShowTeamManager] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: () => base44.entities.Project.list("-created_date"),
  });

  const { data: allTasks = [] } = useQuery({
    queryKey: ["all-tasks"],
    queryFn: () => base44.entities.Task.list(),
  });

  const { data: allPhases = [] } = useQuery({
    queryKey: ["all-phases"],
    queryFn: () => base44.entities.Phase.list(),
  });

  const { data: allTeams = [] } = useQuery({
    queryKey: ["all-teams"],
    queryFn: () => base44.entities.Team.list(),
  });

  const { data: allTeamMembers = [] } = useQuery({
    queryKey: ["all-team-members"],
    queryFn: () => base44.entities.TeamMember.list(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Project.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Project.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setShowForm(false);
      setEditingProject(null);
    },
  });

  const handleSubmit = (data) => {
    if (editingProject) {
      updateMutation.mutate({ id: editingProject.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const calculateProgress = (projectId) => {
    const projectTasks = allTasks.filter(t => t.project_id === projectId);
    if (projectTasks.length === 0) return 0;
    const completed = projectTasks.filter(t => t.status === "completed").length;
    return (completed / projectTasks.length) * 100;
  };

  const getTeamMemberCount = (projectId) => {
    const projectTeams = allTeams.filter(t => t.related_to_type === "project" && t.related_to_id === projectId);
    if (projectTeams.length === 0) return 0;
    const teamIds = projectTeams.map(t => t.id);
    return allTeamMembers.filter(m => teamIds.includes(m.team_id)).length;
  };

  const uniqueOwners = Array.from(new Set(projects.map(p => p.owner).filter(Boolean)));

  const filteredProjects = projects
    .filter(project => {
      const matchesSearch = project.name?.toLowerCase().includes(search.toLowerCase()) ||
                           project.description?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "all" || project.status === statusFilter;
      const matchesOwner = ownerFilter === "all" || project.owner === ownerFilter;
      return matchesSearch && matchesStatus && matchesOwner;
    })
    .sort((a, b) => {
      if (sortBy === "recent") {
        return new Date(b.created_date) - new Date(a.created_date);
      } else if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      } else if (sortBy === "progress") {
        return calculateProgress(b.id) - calculateProgress(a.id);
      }
      return 0;
    });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Prosjekter</h1>
            <p className="text-slate-500 mt-1">{projects.length} prosjekter totalt</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" /> Nytt Prosjekt
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Søk prosjekter..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-white border-0 shadow-sm"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Statuser</SelectItem>
              <SelectItem value="planning">Planlegger</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="on_hold">På Vent</SelectItem>
              <SelectItem value="completed">Fullført</SelectItem>
              <SelectItem value="cancelled">Kansellert</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
              <SelectValue placeholder="Eier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Eiere</SelectItem>
              {uniqueOwners.map((owner) => {
                const user = users.find(u => u.email === owner);
                return (
                  <SelectItem key={owner} value={owner}>
                    {user?.full_name || owner}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
              <SelectValue placeholder="Sorter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Nyeste først</SelectItem>
              <SelectItem value="name">Navn (A-Å)</SelectItem>
              <SelectItem value="progress">Fremdrift</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-56 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-16">
            <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Ingen prosjekter funnet</h3>
            <p className="text-slate-500 mb-6">Kom i gang ved å opprette ditt første prosjekt</p>
            <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Opprett Prosjekt
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProjects.map(project => {
              const progress = calculateProgress(project.id);
              const taskCount = allTasks.filter(t => t.project_id === project.id).length;
              const teamMemberCount = getTeamMemberCount(project.id);
              const statusColors = {
                planning: "bg-slate-100 text-slate-700",
                active: "bg-blue-100 text-blue-700",
                on_hold: "bg-amber-100 text-amber-700",
                completed: "bg-emerald-100 text-emerald-700",
                cancelled: "bg-rose-100 text-rose-700",
              };
              const statusLabels = {
                planning: "Planlegger",
                active: "Aktiv",
                on_hold: "På Vent",
                completed: "Fullført",
                cancelled: "Kansellert",
              };
              
              return (
                <Card 
                  key={project.id}
                  className="p-6 bg-white border-0 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 space-y-3" onClick={() => navigate(createPageUrl(`ProjectDetail?id=${project.id}`))} style={{ cursor: 'pointer' }}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-slate-900 mb-1">{project.name}</h3>
                          {project.description && (
                            <p className="text-sm text-slate-600 line-clamp-2">{project.description}</p>
                          )}
                        </div>
                        <Badge className={statusColors[project.status]}>
                          {statusLabels[project.status]}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        {project.owner_name && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Eier:</span> {project.owner_name}
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProject(project);
                            setShowTeamManager(true);
                          }}
                          className="flex items-center gap-1 hover:text-zinc-900 transition-colors"
                        >
                          <Users className="w-4 h-4" />
                          {teamMemberCount} {teamMemberCount === 1 ? "medlem" : "medlemmer"}
                        </button>
                        <div className="flex items-center gap-1">
                          {taskCount} {taskCount === 1 ? "oppgave" : "oppgaver"}
                        </div>
                      </div>
                    </div>

                    <div className="lg:w-64 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 font-medium">Fremdrift</span>
                        <span className="text-slate-900 font-semibold">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <ProjectForm
          project={editingProject}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingProject(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {showTeamManager && selectedProject && (
        <ProjectTeamManager
          projectId={selectedProject.id}
          projectName={selectedProject.name}
          onClose={() => {
            setShowTeamManager(false);
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
}